import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiLogOut } from "react-icons/fi";
import useAuth from "../useAuth";
import PageName from "../Components/global/PageName";
import {
  FaUserShield,
  FaEnvelope,
  FaPhoneAlt,
  FaCrown,
  FaKey,
  FaShieldAlt,
  FaTimes,
  FaLock,
} from "react-icons/fa";
import { GiSparkles } from "react-icons/gi";
import { toast } from "react-toastify";

export const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogout = () => {
    let userRes = window.confirm("Are you sure you want to log out of your admin session?");
    if (!userRes) return null;
    logout();
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Admin Profile Hub"
        subTitle="Manage account identity, security preferences, and administrative access."
      />

      <div className="max-w-3xl mx-auto px-4 mt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#121215]/90 backdrop-blur-2xl rounded-3xl p-8 md:p-10 border border-[#C8A165]/30 shadow-[0_20px_50px_rgba(0,0,0,0.8)] flex flex-col items-center text-center relative overflow-hidden"
        >
          {/* Header Golden Mesh Halo */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-36 pointer-events-none opacity-40"
            style={{
              background: "radial-gradient(circle, rgba(200,161,101,0.3) 0%, rgba(18,18,21,0) 75%)",
              filter: "blur(30px)",
            }}
          />

          {/* Top Decorative Border Line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C8A165] to-transparent" />

          {/* User Profile Avatar with Glowing Halo */}
          <div className="relative z-10 mt-2 mb-4">
            <div className="w-28 h-28 rounded-full bg-[#0A0A0C] p-1.5 shadow-[0_0_30px_rgba(200,161,101,0.35)] border border-[#C8A165]/40">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-[#C8A165] via-[#F5E5C9] to-[#9E7A48] text-[#0A0A0C] flex items-center justify-center font-extrabold text-4xl uppercase shadow-inner">
                {user?.fullName?.[0] || user?.name?.[0] || "A"}
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-[#18181D] border border-[#C8A165] text-[#C8A165] p-2 rounded-full shadow-lg">
              <GiSparkles size={14} />
            </div>
          </div>

          {/* User Identity Title & Badge */}
          <h2 className="text-2xl font-bold text-[#F8F5F2] font-cormorant tracking-wide capitalize">
            {user?.fullName || user?.name || "Tanushree Admin"}
          </h2>

          <div className="inline-flex items-center gap-1.5 bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/35 text-xs font-extrabold px-4 py-1.5 rounded-full uppercase tracking-widest mt-2.5 shadow-md">
            <FaCrown size={12} className="text-[#C8A165]" /> {user?.userType || "Master Administrator"}
          </div>

          {/* Key Metric Overview Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full mt-8">
            <div className="bg-[#18181D] p-3.5 rounded-2xl border border-[#C8A165]/20 text-center">
              <span className="text-[0.62rem] uppercase font-bold text-[#C8A165] block">Account Status</span>
              <span className="text-xs font-bold text-emerald-400 flex items-center justify-center gap-1 mt-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" /> Active
              </span>
            </div>

            <div className="bg-[#18181D] p-3.5 rounded-2xl border border-[#C8A165]/20 text-center">
              <span className="text-[0.62rem] uppercase font-bold text-[#C8A165] block">Security Level</span>
              <span className="text-xs font-bold text-[#F8F5F2] flex items-center justify-center gap-1 mt-1">
                <FaShieldAlt size={11} className="text-[#C8A165]" /> High SSL
              </span>
            </div>

            <div className="bg-[#18181D] p-3.5 rounded-2xl border border-[#C8A165]/20 text-center col-span-2 sm:col-span-1">
              <span className="text-[0.62rem] uppercase font-bold text-[#C8A165] block">Session Token</span>
              <span className="text-xs font-bold text-[#F8F5F2]/70 flex items-center justify-center gap-1.5 mt-1">
                <FaLock size={10} className="text-[#C8A165]" /> Encrypted JWT
              </span>
            </div>
          </div>

          {/* Account Profile Details Box */}
          <div className="w-full mt-6 bg-[#16161C] p-6 rounded-2xl border border-[#C8A165]/20 space-y-4 text-left">
            <div className="flex items-center justify-between py-2 border-b border-[#C8A165]/15">
              <span className="text-xs font-semibold text-[#C8A165] flex items-center gap-2">
                <FaUserShield className="text-[#C8A165]" /> Full Name
              </span>
              <span className="text-xs font-bold text-[#F8F5F2] capitalize">
                {user?.fullName || user?.name || "N/A"}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-[#C8A165]/15">
              <span className="text-xs font-semibold text-[#C8A165] flex items-center gap-2">
                <FaEnvelope className="text-[#C8A165]" /> Email Address
              </span>
              <span className="text-xs font-bold text-[#F8F5F2]">
                {user?.email || "N/A"}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-[#C8A165]/15">
              <span className="text-xs font-semibold text-[#C8A165] flex items-center gap-2">
                <FaPhoneAlt className="text-[#C8A165]" /> Mobile Number
              </span>
              <span className="text-xs font-bold text-[#F8F5F2]">
                {user?.mobileNo || "+91 98765 43210"}
              </span>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-xs font-semibold text-[#C8A165] flex items-center gap-2">
                <FaCrown className="text-[#C8A165]" /> System Role
              </span>
              <span className="text-xs font-extrabold text-[#C8A165] uppercase">
                {user?.userType || "Master Admin"}
              </span>
            </div>
          </div>

          {/* Action Buttons Strip */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowPasswordModal(true)}
              className="w-full sm:w-auto px-6 py-3 bg-[#18181D] hover:bg-[#C8A165] text-[#C8A165] hover:text-[#0A0A0C] border border-[#C8A165]/35 text-xs font-bold uppercase tracking-wider rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg"
            >
              <FaKey size={12} /> Change Security Key
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleLogout}
              className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white text-xs font-bold uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-900/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Log Out Account</span>
              <FiLogOut size={14} />
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Security Key Change Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#121215] w-full max-w-md rounded-3xl border border-[#C8A165]/30 shadow-[0_20px_50px_rgba(0,0,0,0.9)] p-6 text-left"
            >
              <div className="flex justify-between items-center pb-4 border-b border-[#C8A165]/20 mb-4">
                <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                  <FaKey className="text-[#C8A165]" /> Update Password
                </h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="text-[#F8F5F2]/50 hover:text-white cursor-pointer"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165]"
                  />
                </div>

                <div className="pt-3 flex justify-end gap-3 border-t border-[#C8A165]/15">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-[#F8F5F2]/60 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      toast.success("Security key updated successfully");
                      setShowPasswordModal(false);
                    }}
                    className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider text-[#0A0A0C]"
                    style={{
                      background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                    }}
                  >
                    Update Key
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
